/**
 * Asks for brand details that are still empty, before they are needed: the
 * template editor asks for all of them, an export only for the ones its
 * template prints. Every field shown is required. People who can't change
 * the brand kit see what is missing and who can fill it in.
 */

import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { memo, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { TextArea } from '../../components/inputs/TextArea';
import { Modal } from '../../components/Modal';
import { T, useT, useTranslator } from '../../translation/src';
import { useOrganisationTemplateLibraryUpdateConfigMutation, type TemplateLibraryConfigFieldsFragment } from '../../types/graphqlTypes';
import { BRAND_FIELDS, type BrandDetail, type BrandTextDetail } from './brandDetails';
import { configInputOf } from './configInput';
import { useTemplateUploads } from './useTemplateUploads';

/** What was filled in: the text details and (when uploaded) the new logo's URL — for use right away, before the library refetches. */
export interface BrandDetailsResult {
  brand: Partial<Record<BrandTextDetail, string>>;
  logoUrl: string | null;
}

interface BrandDetailsDialogProps {
  open: boolean;
  organisationId: string;
  config: TemplateLibraryConfigFieldsFragment;
  /** The details to ask for (the editor: all; an export: the missing ones its template prints). */
  details: readonly BrandDetail[];
  /** Why they are asked: the wording of the dialog. */
  reason: 'editor' | 'export';
  /** May change the brand kit (else: what is missing, and the way out). */
  canEdit: boolean;
  onDone: (result: BrandDetailsResult) => void;
  onCancel: () => void;
  /** Exports only: carry on without the missing details (those who can't fill them in). */
  onSkip?: () => void;
}

const BrandDetailsDialog_: React.FC<BrandDetailsDialogProps> = ({ open, organisationId, config, details, reason, canEdit, onDone, onCancel, onSkip }) => {
  const translate = useTranslator();
  const fields = BRAND_FIELDS.filter((field) => details.includes(field.key));
  const askLogo = details.includes('logo');
  const [values, setValues] = useState<Partial<Record<BrandTextDetail, string>>>(() => Object.fromEntries(fields.map((f) => [f.key, config.brand[f.key] ?? ''])));
  const [logoUrl, setLogoUrl] = useState<string | null>(config.logo?.url ?? null);
  const [busy, setBusy] = useState<'save' | 'logo' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const { uploadLogo } = useTemplateUploads(organisationId);
  const [updateConfig] = useOrganisationTemplateLibraryUpdateConfigMutation({ refetchQueries: ['organisationTemplateLibrary', 'projectTemplateLibrary'], awaitRefetchQueries: true });
  const messages = {
    failed: useT('saving failed', { swc: true }),
    invalidImage: useT('the logo must be a PNG, JPEG or SVG file of at most 20 MB', { swc: true }),
  };

  const complete = fields.every((f) => (values[f.key] ?? '').trim() !== '') && (!askLogo || logoUrl !== null);
  const missingNames = [...(askLogo && !config.logo ? [translate('logo', { swc: true })] : []), ...fields.filter((f) => !(config.brand[f.key] ?? '').trim()).map((f) => translate(f.label, { swc: true }))];

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const brand = Object.fromEntries(fields.map((f) => [f.key, (values[f.key] ?? '').trim()]));
      if (fields.length > 0) await updateConfig({ variables: { organisationId, config: { ...configInputOf(config), brand: { ...configInputOf(config).brand, ...brand } } } });
      onDone({ brand, logoUrl: logoUrl !== config.logo?.url ? logoUrl : null });
    } catch {
      setError(messages.failed);
    } finally {
      setBusy(null);
    }
  };

  const pickLogo = async (file: File) => {
    setBusy('logo');
    setError(null);
    try {
      setLogoUrl(await uploadLogo(file));
    } catch {
      setError(messages.invalidImage);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal.Container open={open} onClose={onCancel} panelClassName="max-w-lg w-full" className="relative z-[31]">
      <Modal.Header onClose={onCancel}>
        <T _str="brand details" swc />
      </Modal.Header>
      <div className="flex flex-col gap-4 text-left text-sm">
        <p className="text-gray-600 dark:text-gray-300">
          {reason === 'editor' ? (
            <T _str="templates print your organisation's logo and contact details: fill them in once (the brand kit) before editing" swc />
          ) : (
            <T _str="this template prints brand details that are still empty: fill them in to export" swc />
          )}
        </p>
        {canEdit ? (
          <>
            {askLogo && (
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-32 items-center justify-center rounded border border-gray-200 bg-white p-2 dark:border-gray-600">
                  {logoUrl ? <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-gray-400">{translate('no logo', { swc: true })}</span>}
                </div>
                <Button variant="secondary" size="xs" loading={busy === 'logo'} onClick={() => logoInput.current?.click()}>
                  <ArrowUpTrayIcon className="mr-1 w-4" />
                  {logoUrl ? <T _str="replace logo" swc /> : <T _str="upload logo" swc />}
                </Button>
                <input
                  ref={logoInput}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void pickLogo(file);
                  }}
                />
              </div>
            )}
            {fields.map((field) => (
              <label key={field.key} className="flex flex-col gap-1">
                {translate(field.label, { swc: true })}
                {field.multiline ? (
                  <TextArea value={values[field.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))} className="min-h-[4rem] text-sm" />
                ) : (
                  <Input value={values[field.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))} />
                )}
              </label>
            ))}
          </>
        ) : (
          <div className="flex flex-col gap-1">
            <span className="font-medium">
              <T _str="still empty: {details}" args={{ details: missingNames.join(', ') }} swc />
            </span>
            <span className="text-gray-500">
              <T _str="someone who manages the print templates can fill these in under brand kit" swc />
            </span>
          </div>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-error-red-500/10 px-3 py-2 text-error-red-500">
            {error}
          </p>
        )}
      </div>
      <Modal.Footer className="justify-end gap-2">
        <Button variant="secondary" size="xs" onClick={onCancel}>
          {reason === 'editor' ? <T _str="back to the templates" swc /> : <T _str="cancel" swc />}
        </Button>
        {!canEdit && onSkip && (
          <Button variant="secondary" size="xs" onClick={onSkip}>
            <T _str="export without them" swc />
          </Button>
        )}
        {canEdit && (
          <Button variant="primary" size="xs" loading={busy === 'save'} disabled={!complete || busy !== null} onClick={() => void save()}>
            {reason === 'editor' ? <T _str="save and edit" swc /> : <T _str="save and export" swc />}
          </Button>
        )}
      </Modal.Footer>
    </Modal.Container>
  );
};

export const BrandDetailsDialog = memo(BrandDetailsDialog_);
